import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  CONSENT_VERSION,
  defaultCategoriesForRegion,
  detectGPC,
  detectRegion,
  loadConsent,
  saveConsent,
  type ConsentCategories,
  type Region,
} from "../../../lib/cookieConsent";

interface CookieConsentContextValue {
  region: Region;
  gpc: boolean;
  categories: ConsentCategories;
  bannerOpen: boolean;
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  acknowledge: () => void;
  saveCategories: (categories: ConsentCategories) => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [region] = useState<Region>(() => detectRegion());
  const [gpc] = useState<boolean>(() => detectGPC());
  const defaults = useMemo(() => defaultCategoriesForRegion(region), [region]);

  const [categories, setCategories] = useState<ConsentCategories>(defaults);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = loadConsent();
    if (stored) {
      setCategories(stored.categories);
      setBannerOpen(false);
    } else {
      setCategories(defaults);
      setBannerOpen(true);
    }
    // Runs once on mount — re-running on `defaults` changes would reopen the
    // banner if region/gpc happened to be recomputed, which they never are here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function persist(next: ConsentCategories) {
    setCategories(next);
    saveConsent({ version: CONSENT_VERSION, region, gpc, categories: next, timestamp: new Date().toISOString() });
    setBannerOpen(false);
    setPreferencesOpen(false);
  }

  const value: CookieConsentContextValue = {
    region,
    gpc,
    categories,
    bannerOpen,
    preferencesOpen,
    openPreferences: () => {
      setBannerOpen(false);
      setPreferencesOpen(true);
    },
    closePreferences: () => setPreferencesOpen(false),
    acceptAll: () => persist({ functional: true, analytics: true }),
    rejectAll: () => persist({ functional: false, analytics: false }),
    acknowledge: () => persist(categories),
    saveCategories: (next) => persist(next),
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  return ctx;
}

// For future analytics/marketing script integrations: check this before
// injecting any non-essential script or tag, per the banner spec's "prior
// blocking" requirement. No such scripts are wired up in this codebase yet.
export function useHasConsent(category: keyof ConsentCategories): boolean {
  return useCookieConsent().categories[category];
}
